/* =========================================================
   CART FUNCTION – OWNER VERSION (v2, hardened)
   - Precieze Kaching-detectie (via event.submitter, geen false positives)
   - Netwerk-veerkracht (timeout + ok-checks overal)
   - Volledige foutafhandeling, geen permanent slot
   ========================================================= */

(function () {
  "use strict";

  // Registreer listeners maar één keer, ook als het bestand 2x laadt.
  if (window.__cartFunctionInit) return;
  window.__cartFunctionInit = true;

  // Slot tegen dubbele/parallelle submits. Wordt ALTIJD vrijgegeven (finally).
  let isSubmitting = false;

  const REQUEST_TIMEOUT_MS = 10000;

  /* -----------------------------------------
     Utilities
  ----------------------------------------- */
  function triggerEvent(name, detail = {}) {
    return document.dispatchEvent(
      new CustomEvent(name, { detail, cancelable: true })
    );
  }

  function enableScroll() {
    document.body.style.overflow = "auto";
  }

  // Laat het thema desgewenst een mooie melding tonen; anders fallback alert().
  function reportError(error) {
    console.error("Cart error:", error);
    const message = error?.description || "Unable to add item to cart.";
    const notPrevented = triggerEvent("cart:error", { error, message });
    if (notPrevented) alert(message); // niemand ving 'cart:error' op
  }

  // Eén plek voor timeout + foutafhandeling.
  async function request(url, options = {}) {
    const res = await fetch(url, {
      ...options,
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      headers: { Accept: "application/json", ...(options.headers || {}) },
    });
    if (!res.ok) {
      throw await res
        .json()
        .catch(() => ({ description: res.statusText || "Request failed" }));
    }
    return res.json();
  }

  /* -----------------------------------------
     CART API HELPERS
  ----------------------------------------- */
  function addToCart(formData) {
    return request("/cart/add.js", { method: "POST", body: formData });
  }

  function updateCart(updates) {
    return request("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ updates }),
    });
  }

  function fetchCart() {
    return request("/cart.js");
  }

  /* -----------------------------------------
     AL-AFGEHANDELD-DETECTIE
     Op deze winkel doet de <product-form> web component van je thema zelf al
     de AJAX add-to-cart. Kaching schrijft alleen in datzelfde formulier
     (verborgen input name="properties[__kaching_bundles]"); er is GEEN apart
     Kaching-element om uit te sluiten. We laten cart-function.js daarom de
     formulieren met rust die al door een ander systeem worden afgehandeld,
     anders krijg je twee adds van dezelfde klik (2 wordt 4).
  ----------------------------------------- */
  function isHandledElsewhere(form, event) {
    // Een ander handler (bv. <product-form>) heeft de submit al geclaimd.
    if (event.defaultPrevented) return true;
    // Dit formulier wordt beheerd door de <product-form> web component.
    if (form.closest("product-form")) return true;
    // Extra zekerheid: het formulier draagt de Kaching-bundel-property.
    if (form.querySelector('[name*="kaching"]')) return true;
    return false;
  }

  /* -----------------------------------------
     ADD TO CART HANDLER
  ----------------------------------------- */
  document.addEventListener("submit", async (event) => {
    const form = event.target;
    if (!form.matches("form[action*='/cart/add']")) return;

    // Laat formulieren met rust die al door een ander systeem (de
    // <product-form> web component of Kaching) worden afgehandeld; anders
    // krijg je een dubbele toevoeging (2 wordt 4). Wij openen alleen netjes de
    // drawer zodra de inhoud is bijgewerkt.
    if (isHandledElsewhere(form, event)) {
      armDrawerOpen();
      return;
    }

    event.preventDefault();

    if (isSubmitting) return;
    isSubmitting = true;

    const submitButton = form.querySelector('[type="submit"]');
    const formData = new FormData(form);

    try {
      if (submitButton) submitButton.setAttribute("disabled", "disabled");

      const item = await addToCart(formData);
      const cart = await fetchCart();

      triggerEvent("cart:item-added", { item, cart });
      triggerEvent("cart:refresh", { cart, openDrawer: true });
      enableScroll();
    } catch (error) {
      reportError(error);
    } finally {
      if (submitButton) submitButton.removeAttribute("disabled");
      isSubmitting = false;
    }
  });

  /* -----------------------------------------
     QUANTITY CHANGE
  ----------------------------------------- */
  document.addEventListener("click", async (event) => {
    const btn = event.target.closest("[data-cart-quantity]");
    if (!btn) return;
    event.preventDefault();

    const line = btn.dataset.line;
    const qty = parseInt(btn.dataset.quantity, 10);
    if (!line || Number.isNaN(qty)) return;

    try {
      btn.setAttribute("disabled", "disabled");
      const cart = await updateCart({ [line]: qty });
      triggerEvent("cart:refresh", { cart });
    } catch (error) {
      reportError(error);
    } finally {
      btn.removeAttribute("disabled");
    }
  });

  /* -----------------------------------------
     REMOVE FROM CART
  ----------------------------------------- */
  document.addEventListener("click", async (event) => {
    const btn = event.target.closest("[data-cart-remove]");
    if (!btn) return;
    event.preventDefault();

    const line = btn.dataset.line;
    if (!line) return;

    try {
      btn.setAttribute("disabled", "disabled");
      const cart = await updateCart({ [line]: 0 });
      triggerEvent("cart:item-removed", { cart });
      triggerEvent("cart:refresh", { cart });
    } catch (error) {
      reportError(error);
    } finally {
      btn.removeAttribute("disabled");
    }
  });

  /* -----------------------------------------
     CART DRAWER + TELLER
  ----------------------------------------- */
  document.addEventListener("cart:refresh", (e) => {
    const cart = e.detail?.cart;
    if (!cart || typeof cart.item_count !== "number") return;

    document
      .querySelectorAll("[data-cart-count]")
      .forEach((el) => (el.textContent = cart.item_count));

    // Open de drawer alleen na een toevoeging, niet bij elke wijziging.
    if (e.detail.openDrawer) {
      const drawer = document.querySelector("cart-drawer");
      if (drawer && typeof drawer.open === "function") drawer.open();
    }
  });

  /* -----------------------------------------
     DRAWER OPENEN NA EEN ADD DOOR <product-form>
     Op productpagina's doet cart-function.js de add niet meer zelf. Daarom
     luisteren we mee op ELKE geslaagde POST naar /cart/add (door wie dan ook:
     <product-form> of Kaching) en openen we dan de drawer + verversen de
     teller. De fetch wordt maar één keer omwikkeld (de IIFE draait dankzij de
     init-guard slechts één keer).
  ----------------------------------------- */
  /* -----------------------------------------
     DRAWER OPENEN NA EEN ADD DOOR <product-form>
     Op productpagina's doet cart-function.js de add niet meer zelf. We raken
     window.fetch NIET aan. In plaats daarvan "wapenen" we het openen wanneer
     JIJ op add klikt (zie de submit-handler hierboven) en openen we de drawer
     pas zodra het thema de INHOUD ervan heeft bijgewerkt. Zo zie je niet eerst
     even "cart is empty".
  ----------------------------------------- */
  let drawerObserver = null;

  function armDrawerOpen() {
    const drawer = document.querySelector("cart-drawer");
    if (!drawer || typeof drawer.open !== "function") return;

    if (drawerObserver) drawerObserver.disconnect();

    let done = false;
    let settle = null;
    let fallback = null;

    const finish = () => {
      if (done) return;
      done = true;
      if (drawerObserver) {
        drawerObserver.disconnect();
        drawerObserver = null;
      }
      clearTimeout(settle);
      clearTimeout(fallback);
      drawer.open();
      // Werk losse tellers bij (bv. het mandje in de header).
      fetchCart()
        .then((cart) => triggerEvent("cart:refresh", { cart }))
        .catch(() => {});
    };

    // Open zodra het thema de inhoud van de drawer (her)rendert.
    drawerObserver = new MutationObserver(() => {
      clearTimeout(settle);
      settle = setTimeout(finish, 60); // wacht tot de mutaties tot rust komen
    });
    drawerObserver.observe(drawer, { childList: true, subtree: true });

    // Vangnet: open sowieso na 2000ms, ook als er geen mutatie binnenkomt.
    fallback = setTimeout(finish, 2000);
  }

  /* -----------------------------------------
     Het oude FREE GIFT-blok is bewust weggelaten:
     Kaching regelt gifts zelf; auto-toevoegen bij cart:refresh
     veroorzaakte oplopende item-aantallen.
  ----------------------------------------- */
})();